import React from 'react';
import {Alert, Box, CircularProgress, Stack, Tooltip, Typography} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import IconButton from '@mui/material/IconButton';
import SourcesMediaTranscriptLayout from './SourcesMediaTranscriptLayout';
import SourcesImageLayout from './SourcesImageLayout';
import SourcesPdfDocumentSplitView from './SourcesPdfDocumentSplitView';
import ReadonlyProseMirrorRenderer from './ReadonlyProseMirrorRenderer';
import CableDocViewer from './CableDocViewer';
import DualLanguagePanes from './DualLanguagePanes';
import SourcesFileProcessingProgress from './SourcesFileProcessingProgress';
import SourceFileDraftSelectionHost from './SourceFileDraftSelectionHost';
import {PmDocumentNotReadyError, fetchSourcePm, fetchDataItemSegments, getRawStreamUrl} from './sourcesApi';
import {languageDirection} from './sourcesLanguage';
import {isFileActivelyProcessing, resolveFileProcessingStatus} from './sourcesFileMetadata';
import useSourceItemProcessingPoll from './useSourceItemProcessingPoll';
import {useSession} from '../../../../../firebase/UserProvider';

const isMediaMime = (mimeType) =>
    typeof mimeType === 'string'
    && (mimeType.startsWith('audio/') || mimeType.startsWith('video/')
        || mimeType === 'audio' || mimeType === 'video');

const isImageMime = (mimeType, fileType) =>
    (typeof mimeType === 'string'
        && (mimeType.startsWith('image/') || mimeType === 'image' || mimeType === 'product'))
    || fileType === 'image';

const isPdfMime = (mimeType, fileType) =>
    mimeType === 'application/pdf' || mimeType === 'pdf' || fileType === 'pdf';

const LANGUAGE_LABELS = {
    es: 'Spanish', fr: 'French', de: 'German', zh: 'Chinese', ar: 'Arabic', ru: 'Russian',
    ja: 'Japanese', ko: 'Korean', pt: 'Portuguese', it: 'Italian', nl: 'Dutch', pl: 'Polish',
    tr: 'Turkish', vi: 'Vietnamese', th: 'Thai', hi: 'Hindi', he: 'Hebrew', fa: 'Persian', pe: 'Persian',
    uk: 'Ukrainian',
};

const languageLabel = (code) => {
    if (!code) return 'Original';
    return LANGUAGE_LABELS[code.toLowerCase()] || code.toUpperCase();
};

/**
 * M3 viewer — routes by file type:
 *  audio/* | video/* → SourcesMediaTranscriptLayout (player + synced transcript)
 *  image/*           → SourcesImageLayout (original image + LLM caption/OCR text below)
 *  application/pdf   → SourcesPdfDocumentSplitView (PM left / annotated PDF with bbox overlays right)
 *  everything else   → ReadonlyProseMirrorRenderer (PM only)
 *
 * Raw media is always streamed through dash-api (/raw/stream) — no S3/MinIO
 * URLs are ever exposed to the browser.
 */
const SourcesFileRenderer = ({reportId = null, file, fileDetails, isLoading: isLoadingDetails, error: detailError, sentenceHit = null, savedHighlights = null, activeHighlightSegmentIds = null, activeHighlightRanges = null, onCreateHighlight = null, searchMatches = null, activeSearchMatchIdx = -1, onPmContentReady = null, onDualPaneChange = null, hideToolbar = false}) => {
    const {user} = useSession() || {};
    const userId = user?.uid || null;

    const referenceId = fileDetails?.id || file?.fileId || null;
    const streamId = fileDetails?.referencedDataItemId
        || file?.canonicalFileId
        || referenceId;
    const mimeType = fileDetails?.mimeType || file?.mimeType || null;
    const resolvedFileType = fileDetails?.fileType || file?.fileType || 'unknown';

    const mediaDurationMs = fileDetails?.meta?.processing?.mediaDurationMs ?? null;
    const hasPoster = Boolean(fileDetails?.meta?.processing?.posterS3Key);
    const posterUrl = hasPoster && streamId && userId
        ? `/api/data-items/${streamId}/poster?userId=${encodeURIComponent(userId)}`
        : null;

    // Segment stubs: [{id, numberInItem, pmBlockIndex}] — loaded once when fileDetails arrives.
    // pmBlockIndex (content_path[0]) is the PM block a segment belongs to; numberInItem is only a
    // fallback for older payloads (it counts SENTENCES on sentence-segmented docs, not blocks, so
    // mapping by it stored highlights against the wrong paragraph). First segment of each block
    // wins so the FK points at the paragraph start.
    const [segmentsByBlockIndex, setSegmentsByBlockIndex] = React.useState(null);
    React.useEffect(() => {
        if (!referenceId || !userId || !onCreateHighlight) {
            setSegmentsByBlockIndex(null);
            return undefined;
        }
        const controller = new AbortController();
        fetchDataItemSegments({referenceId, userId, signal: controller.signal})
            .then((stubs) => {
                const map = {};
                (stubs || []).forEach((s) => {
                    const key = s.pmBlockIndex != null ? s.pmBlockIndex : s.numberInItem;
                    if (key != null && map[key] == null) map[key] = s.id;
                });
                setSegmentsByBlockIndex(map);
            })
            .catch((err) => { if (err?.name !== 'AbortError') console.warn('[SourcesFileRenderer] segments fetch failed:', err); });
        return () => controller.abort();
    }, [referenceId, userId, onCreateHighlight]);

    // Called by ReadonlyProseMirrorRenderer when a block is clicked with the highlight button.
    const handleBlockHighlight = React.useCallback((arg) => {
        if (!onCreateHighlight || !segmentsByBlockIndex) return;
        // Accept either a bare block index (legacy/full-segment) or a selection descriptor
        // { blockIndex, startOffset, endOffset, text } for a sub-sentence TEXT_RANGE highlight.
        const sel = typeof arg === 'object' && arg != null ? arg : {blockIndex: arg};
        const blockIndex = sel.blockIndex;
        // Image-derived docs can have more PM blocks than segments (e.g. multiple bullet lists),
        // so segmentsByBlockIndex[blockIndex] may be missing. The DB requires a valid segment FK,
        // but display keys on regionData.blockIndex (below), so any segment of this item works as
        // the anchor. Prefer the exact block's segment, else fall back to the first known segment.
        let segmentId = segmentsByBlockIndex[blockIndex];
        if (segmentId == null) {
            const anyId = Object.values(segmentsByBlockIndex)[0];
            segmentId = anyId != null ? anyId : null;
        }
        if (segmentId == null) return;
        const hasRange = Number.isInteger(sel.startOffset)
            && Number.isInteger(sel.endOffset)
            && sel.endOffset > sel.startOffset;
        if (hasRange) {
            onCreateHighlight({
                segmentId,
                regionType: 'TEXT_RANGE',
                regionData: {
                    blockIndex,
                    startOffset: sel.startOffset,
                    endOffset: sel.endOffset,
                    ...(sel.text ? {text: sel.text} : {}),
                },
            });
        } else {
            onCreateHighlight({segmentId, regionType: 'FULL_SEGMENT', regionData: {blockIndex}});
        }
    }, [onCreateHighlight, segmentsByBlockIndex]);

    const [pmDoc, setPmDoc] = React.useState(null);
    const [originalPmDoc, setOriginalPmDoc] = React.useState(null);
    const [isLoadingPm, setIsLoadingPm] = React.useState(false);
    const [pmError, setPmError] = React.useState(null);
    const [pmNotReady, setPmNotReady] = React.useState(false);
    const [originalLanguage, setOriginalLanguage] = React.useState(null);
    const [hasTranslation, setHasTranslation] = React.useState(false);
    const pmRetryTokenRef = React.useRef(0);

    const loadPmDocument = React.useCallback(async ({signal, retryToken}) => {
        if (!referenceId || !userId) {
            return;
        }
        setIsLoadingPm(true);
        setPmError(null);
        setPmNotReady(false);
        setPmDoc(null);
        setOriginalPmDoc(null);
        try {
            // Omit role so the backend serves English by default (translated when one
            // exists, otherwise the English original). availableRoles tells us whether
            // a separate foreign-language original is worth fetching for the side pane.
            const english = await fetchSourcePm({referenceId, userId, role: undefined, signal});
            if (signal.aborted || retryToken !== pmRetryTokenRef.current) {
                return;
            }

            const servedRole = english?.role ? String(english.role).toLowerCase() : 'original';
            const roles = Array.isArray(english?.availableRoles) ? english.availableRoles : [];
            const detectedLanguage = english?.originalLanguage || null;
            // A foreign original exists when the server served the translated doc and an
            // 'original' role is also available (the original is the non-English source).
            const wantsOriginal = servedRole === 'translated' && roles.includes('original');

            setPmDoc(english?.content || null);
            setOriginalLanguage(detectedLanguage);
            setPmNotReady(false);
            onPmContentReady?.(english?.content || null);

            if (wantsOriginal) {
                try {
                    const original = await fetchSourcePm({referenceId, userId, role: 'original', signal});
                    if (signal.aborted || retryToken !== pmRetryTokenRef.current) {
                        return;
                    }
                    setOriginalPmDoc(original?.content || null);
                    setHasTranslation(Boolean(original?.content));
                } catch (originalErr) {
                    // The English pane already loaded — degrade gracefully to single pane.
                    if (originalErr?.name !== 'AbortError' && !signal.aborted) {
                        setOriginalPmDoc(null);
                        setHasTranslation(false);
                    }
                }
            } else {
                setOriginalPmDoc(null);
                setHasTranslation(false);
            }
        } catch (err) {
            if (err?.name === 'AbortError' || signal.aborted || retryToken !== pmRetryTokenRef.current) {
                return;
            }
            if (err instanceof PmDocumentNotReadyError) {
                setPmNotReady(true);
                setPmError(null);
                return;
            }
            setPmError(err?.message || 'Failed to load transcript.');
        } finally {
            if (!signal.aborted && retryToken === pmRetryTokenRef.current) {
                setIsLoadingPm(false);
            }
        }
    }, [referenceId, userId]);

    const handleProcessingComplete = React.useCallback(({failed, errorMessage} = {}) => {
        if (failed) {
            setPmNotReady(false);
            setPmError(errorMessage || 'Processing failed.');
            return;
        }
        pmRetryTokenRef.current += 1;
        const retryToken = pmRetryTokenRef.current;
        const controller = new AbortController();
        loadPmDocument({signal: controller.signal, retryToken});
    }, [loadPmDocument]);

    const {mergedFile, processingStatus} = useSourceItemProcessingPoll({
        fileId: referenceId,
        userId,
        fileDetails,
        enabled: Boolean(referenceId && userId && !detailError),
        forcePoll: pmNotReady,
        onProcessingComplete: handleProcessingComplete,
    });

    // A processed/failed item is never "processing" — reopening it only fetches the
    // already-built PM, which is a content-loading state, not a pipeline state.
    const pipelineComplete = React.useMemo(
        () => resolveFileProcessingStatus(mergedFile)?.kind === 'ready',
        [mergedFile],
    );

    const showProcessingState = Boolean(
        processingStatus?.kind === 'processing'
        || (pmNotReady && !pipelineComplete)
        || (isFileActivelyProcessing(mergedFile) && !pmDoc && !pmError),
    );

    const effectiveProcessingStatus = React.useMemo(() => {
        if (!showProcessingState) {
            return null;
        }
        if (processingStatus?.kind === 'processing') {
            return processingStatus;
        }
        const fromItem = resolveFileProcessingStatus(mergedFile);
        if (fromItem?.kind === 'processing') {
            return fromItem;
        }
        return {
            kind: 'processing',
            label: 'Processing',
            showSpinner: true,
            errorMessage: null,
            progressPercent: fromItem?.progressPercent ?? null,
        };
    }, [showProcessingState, processingStatus, mergedFile]);

    const rawUrl = React.useMemo(
        () => getRawStreamUrl({referenceId: streamId, userId, disposition: 'inline'}),
        [streamId, userId],
    );

    const showMedia = isMediaMime(mimeType) || resolvedFileType === 'audio' || resolvedFileType === 'video';
    const showImage = !showMedia && isImageMime(mimeType, resolvedFileType);
    const showPdf = isPdfMime(mimeType, resolvedFileType);

    // M6 federated-search deep link: seek media / scroll-highlight extracted text on open.
    const anchorMediaStartMs = sentenceHit?.mediaStartMs ?? null;
    // The hit's top-level block index (contentPath[0]) maps to the renderer's positional
    // block key, which the PM renderer / PDF split view already highlight + scroll to.
    const anchorBlockKey = Array.isArray(sentenceHit?.contentPath) && sentenceHit.contentPath.length > 0
        ? sentenceHit.contentPath[0]
        : null;

    React.useEffect(() => {
        // New file selected → clear dual-language state.
        setOriginalLanguage(null);
        setHasTranslation(false);
        setOriginalPmDoc(null);
        onPmContentReady?.(null);
    }, [referenceId, onPmContentReady]);

    React.useEffect(() => {
        if (!referenceId || !userId || detailError) {
            setPmDoc(null);
            setOriginalPmDoc(null);
            setPmNotReady(false);
            return undefined;
        }
        pmRetryTokenRef.current += 1;
        const retryToken = pmRetryTokenRef.current;
        const controller = new AbortController();
        loadPmDocument({signal: controller.signal, retryToken});
        return () => controller.abort();
    }, [referenceId, userId, detailError, loadPmDocument]);

    const showDualLanguage = hasTranslation && Boolean(originalPmDoc) && !showProcessingState;
    const originalLanguageLabel = languageLabel(originalLanguage);
    const originalDir = languageDirection(originalLanguage);

    // Notify parent whenever the dual-pane state changes so it can reposition the search bar.
    React.useEffect(() => {
        onDualPaneChange?.(showDualLanguage);
    }, [showDualLanguage, onDualPaneChange]);

    // Enables manual Wikidata linking of unlinked mentions in the DEFAULT (English/enrichment) pane —
    // the only doc whose mentions the backend can patch. Never enable it on the foreign-original pane.
    const linkContext = React.useMemo(
        () => (referenceId && userId ? {referenceId, userId, enabled: true} : null),
        [referenceId, userId],
    );

    const draftContext = React.useMemo(() => {
        if (!reportId || !referenceId) return null;
        return {
            reportId,
            referenceId,
            docTitle: fileDetails?.name || file?.fileName || '',
            docUrl: rawUrl,
            segmentsByBlockIndex,
        };
    }, [file?.fileName, fileDetails?.name, rawUrl, referenceId, reportId, segmentsByBlockIndex]);

    const handleDownloadRaw = React.useCallback(() => {
        if (!streamId || !userId) return;
        const url = getRawStreamUrl({referenceId: streamId, userId, disposition: 'attachment'});
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, [streamId, userId]);

    if (isLoadingDetails) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" spacing={2} p={4}>
                <CircularProgress aria-label="Loading file"/>
                <Typography variant="body2" color="text.secondary">Loading file…</Typography>
            </Stack>
        );
    }

    if (detailError && !fileDetails) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4} spacing={1.5}>
                <Alert severity="error" sx={{maxWidth: 420}}>
                    {detailError}
                </Alert>
                <Typography variant="caption" color="text.secondary">
                    This file may have been deleted. Close the viewer and select another file.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
            {!hideToolbar && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={(theme) => ({
                        px: 2,
                        minHeight: 48,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Typography variant="subtitle2" noWrap sx={{flex: 1, minWidth: 0}} title={fileDetails?.name || file?.fileName}>
                        {fileDetails?.name || file?.fileName || ''}
                    </Typography>
                    <Tooltip title="Download" arrow placement="left">
                        <IconButton size="small" onClick={handleDownloadRaw} aria-label="Download" sx={{flexShrink: 0}}>
                            <DownloadOutlinedIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            {detailError && (
                <Alert severity="error" sx={{m: 2, flexShrink: 0}}>{detailError}</Alert>
            )}

            {showMedia && (
                <SourcesMediaTranscriptLayout
                    src={rawUrl}
                    mimeType={mimeType}
                    fileType={resolvedFileType}
                    pmContent={pmDoc?.content ?? null}
                    originalPmContent={showDualLanguage ? (originalPmDoc?.content ?? null) : null}
                    originalLanguageLabel={originalLanguageLabel}
                    originalDir={originalDir}
                    isLoadingMedia={false}
                    isLoadingTranscript={isLoadingPm && !showProcessingState}
                    mediaError={null}
                    transcriptError={showProcessingState ? null : pmError}
                    processingStatus={showProcessingState ? effectiveProcessingStatus : null}
                    seekToMs={anchorMediaStartMs}
                    onBlockHighlight={handleBlockHighlight}
                    highlightedSegmentIds={activeHighlightSegmentIds}
                    highlightedRanges={activeHighlightRanges}
                    linkContext={linkContext}
                    searchMatches={searchMatches}
                    activeSearchMatchIdx={activeSearchMatchIdx}
                    draftContext={draftContext}
                    mediaDurationMs={mediaDurationMs}
                    posterUrl={posterUrl}
                />
            )}

            {showImage && !showMedia && (
                <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    <SourcesImageLayout
                        src={rawUrl}
                        alt={fileDetails?.name || file?.fileName || 'Uploaded image'}
                        pmContent={pmDoc}
                        originalPmContent={showDualLanguage ? originalPmDoc : null}
                        originalLanguageLabel={originalLanguageLabel}
                        originalDir={originalDir}
                        isLoadingText={isLoadingPm && !showProcessingState}
                        textError={showProcessingState ? null : pmError}
                        processingStatus={showProcessingState ? effectiveProcessingStatus : null}
                        onBlockHighlight={handleBlockHighlight}
                        highlightedSegmentIds={activeHighlightSegmentIds}
                        highlightedRanges={activeHighlightRanges}
                        linkContext={linkContext}
                        searchMatches={searchMatches}
                        activeSearchMatchIdx={activeSearchMatchIdx}
                        draftContext={draftContext}
                    />
                </Box>
            )}

            {showPdf && !showMedia && !showImage && (
                <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    <SourcesPdfDocumentSplitView
                        referenceId={referenceId}
                        userId={userId}
                        pmContent={pmDoc}
                        isLoadingPm={isLoadingPm && !showProcessingState}
                        pmError={showProcessingState ? null : pmError}
                        processingStatus={showProcessingState ? effectiveProcessingStatus : null}
                        anchorBlockKey={anchorBlockKey}
                        onBlockHighlight={handleBlockHighlight}
                        highlightedSegmentIds={activeHighlightSegmentIds}
                        highlightedRanges={activeHighlightRanges}
                        linkContext={linkContext}
                        searchMatches={searchMatches}
                        activeSearchMatchIdx={activeSearchMatchIdx}
                        draftContext={draftContext}
                    />
                </Box>
            )}

            {!showMedia && !showImage && !showPdf && (
                showProcessingState ? (
                    <SourcesFileProcessingProgress status={effectiveProcessingStatus}/>
                ) : isLoadingPm ? (
                    <Stack flex={1} alignItems="center" justifyContent="center" spacing={2} p={4}>
                        <CircularProgress size={28}/>
                        <Typography variant="body2" color="text.secondary">Loading content…</Typography>
                    </Stack>
                ) : pmError ? (
                    <Alert severity="error" sx={{m: 2}}>{pmError}</Alert>
                ) : pmDoc ? (
                    showDualLanguage ? (
                        <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                            <SourceFileDraftSelectionHost
                                draftContext={draftContext}
                                sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}
                            >
                                <DualLanguagePanes
                                    leftLabel="English"
                                    rightLabel={originalLanguageLabel}
                                    left={<Box sx={{p: 3, pr: 5}}>{pmDoc?.attrs?.cableDoc ? <CableDocViewer pmDoc={pmDoc} searchMatches={searchMatches} activeSearchMatchIdx={activeSearchMatchIdx} onBlockHighlight={handleBlockHighlight}/> : <ReadonlyProseMirrorRenderer content={pmDoc} highlightedBlockKey={anchorBlockKey} highlightedSegmentIds={activeHighlightSegmentIds} highlightedRanges={activeHighlightRanges} onBlockHighlight={handleBlockHighlight} linkContext={linkContext} searchMatches={searchMatches} activeSearchMatchIdx={activeSearchMatchIdx}/>}</Box>}
                                    right={<Box sx={{p: 3}}><ReadonlyProseMirrorRenderer content={originalPmDoc} dir={originalDir}/></Box>}
                                />
                            </SourceFileDraftSelectionHost>
                        </Box>
                    ) : (
                        <SourceFileDraftSelectionHost draftContext={draftContext} sx={{flex: 1, overflowY: 'auto'}}>
                            <Box sx={{p: 3, pr: 5}}>
                                {pmDoc?.attrs?.cableDoc
                                    ? <CableDocViewer pmDoc={pmDoc} searchMatches={searchMatches} activeSearchMatchIdx={activeSearchMatchIdx} onBlockHighlight={handleBlockHighlight}/>
                                    : <ReadonlyProseMirrorRenderer content={pmDoc} highlightedBlockKey={anchorBlockKey} highlightedSegmentIds={activeHighlightSegmentIds} highlightedRanges={activeHighlightRanges} onBlockHighlight={handleBlockHighlight} linkContext={linkContext} searchMatches={searchMatches} activeSearchMatchIdx={activeSearchMatchIdx}/>
                                }
                            </Box>
                        </SourceFileDraftSelectionHost>
                    )
                ) : (
                    <Stack flex={1} alignItems="center" justifyContent="center" p={4} spacing={1}>
                        <Typography variant="body2" color="text.secondary">No content available.</Typography>
                    </Stack>
                )
            )}
        </Stack>
    );
};

export default SourcesFileRenderer;
